import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { ArrowLeft, MessageCircle, Phone, Calendar, Award } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'sonner';
import { getAvatarUrl } from '../utils/avatarUrl';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const LevelIndicator = ({ level }) => {
  const levelNames = ['Principiante', 'Base', 'Intermedio', 'Avanzato', 'Esperto'];
  
  return (
    <div>
      <div className="flex gap-1.5 mb-1">
        {[1, 2, 3, 4, 5].map((i) => (
          <div
            key={i}
            className={`w-8 h-2 rounded-full ${
              i <= level ? 'bg-sky-400' : 'bg-slate-200'
            }`}
          />
        ))}
      </div>
      <p className="text-sm text-slate-600">{levelNames[level - 1] || 'Non specificato'}</p>
    </div>
  );
};

const PlayerDetailPage = () => {
  const { userId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [player, setPlayer] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchPlayer();
  }, [userId]);

  const fetchPlayer = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/users/${userId}`, {
        withCredentials: true
      });
      setPlayer(response.data);
    } catch (error) {
      console.error('Error fetching player:', error);
      toast.error('Giocatore non trovato');
      navigate('/players');
    } finally {
      setLoading(false);
    }
  };

  const handleStartChat = async () => {
    try {
      const response = await axios.post(
        `${API_URL}/api/conversations`,
        { user_id: userId },
        { withCredentials: true }
      );
      navigate(`/chat/${response.data.conversation_id}`);
    } catch (error) {
      toast.error('Errore nell\'avvio della chat');
    }
  };

  if (loading) {
    return (
      <div className="p-6">
        <div className="h-64 bg-slate-100 rounded-3xl animate-pulse" />
      </div>
    );
  }

  if (!player) return null;

  const isOwnProfile = player.user_id === user?.user_id;

  return (
    <div className="p-4 sm:p-6 lg:p-8 max-w-2xl mx-auto animate-fadeIn" data-testid="player-detail-page">
      {/* Header */}
      <div className="flex items-center gap-4 mb-6">
        <button
          onClick={() => navigate(-1)}
          className="w-10 h-10 flex items-center justify-center rounded-full bg-white border border-slate-200 hover:bg-slate-50 transition-colors"
        >
          <ArrowLeft className="w-5 h-5 text-slate-600" />
        </button>
        <h1 className="font-outfit text-xl font-semibold text-slate-900">
          Profilo Giocatore
        </h1>
      </div>

      {/* Profile Card */}
      <div className="bg-white rounded-3xl p-8 border border-slate-100 mb-6 text-center">
        <div className="w-24 h-24 bg-sky-400 rounded-3xl flex items-center justify-center text-white text-3xl font-semibold mx-auto mb-4 overflow-hidden">
          {getAvatarUrl(player) ? (
            <img src={getAvatarUrl(player)} alt={player.name} className="w-full h-full rounded-3xl object-cover" />
          ) : (
            player.name?.charAt(0)
          )}
        </div>
        
        <h2 className="font-outfit text-2xl font-semibold text-slate-900 mb-1">
          {player.name} {player.surname || ''}
        </h2>
        
        {player.age && (
          <p className="text-slate-500 mb-4">{player.age} anni</p>
        )}

        {!isOwnProfile && (
          <button
            onClick={handleStartChat}
            data-testid="start-chat-btn"
            className="inline-flex items-center gap-2 bg-sky-400 text-white rounded-full px-6 py-3 font-medium hover:bg-sky-500 transition-all hover:-translate-y-0.5 hover:shadow-lg"
          >
            <MessageCircle className="w-5 h-5" />
            Invia messaggio
          </button>
        )}

        {isOwnProfile && (
          <button
            onClick={() => navigate('/profile')}
            className="inline-flex items-center gap-2 bg-slate-100 text-slate-700 rounded-full px-6 py-3 font-medium hover:bg-slate-200 transition-all"
          >
            Modifica profilo
          </button>
        )}
      </div>

      {/* Info Cards */}
      <div className="grid gap-4 sm:grid-cols-2">
        {/* Level */}
        <div className="bg-white rounded-3xl p-6 border border-slate-100">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 bg-sky-100 rounded-xl flex items-center justify-center">
              <Award className="w-5 h-5 text-sky-500" />
            </div>
            <p className="text-slate-500">Livello</p>
          </div>
          <LevelIndicator level={player.level || 1} />
        </div>

        {/* Phone */}
        {player.phone && (
          <div className="bg-white rounded-3xl p-6 border border-slate-100">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 bg-green-100 rounded-xl flex items-center justify-center">
                <Phone className="w-5 h-5 text-green-500" />
              </div>
              <p className="text-slate-500">Telefono</p>
            </div>
            <p className="font-medium text-slate-900">{player.phone}</p>
          </div>
        )}

        {/* Member since */}
        <div className="bg-white rounded-3xl p-6 border border-slate-100">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 bg-purple-100 rounded-xl flex items-center justify-center">
              <Calendar className="w-5 h-5 text-purple-500" />
            </div>
            <p className="text-slate-500">Membro dal</p>
          </div>
          <p className="font-medium text-slate-900">
            {player.created_at 
              ? new Date(player.created_at).toLocaleDateString('it-IT', { month: 'long', year: 'numeric' })
              : 'Non disponibile'
            }
          </p>
        </div>
      </div>
    </div>
  );
};

export default PlayerDetailPage;
